import type { ResearchLoop } from "../lib/graph-types";

interface Props {
  loop: ResearchLoop;
}

export function ResearchLoopPanel({ loop }: Props) {
  return (
    <section className="p-6 border-b border-fg">
      <h2 className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-4">
        // Research loop · discover → map → improve → verify → rescore
      </h2>

      <div className="flex flex-wrap gap-2 items-stretch mb-6">
        {loop.steps.map((step, i) => (
          <div key={step.id} className="flex items-center gap-2">
            <a
              href={step.href}
              className={`border border-fg px-3 py-2 min-w-[120px] hover:bg-fg hover:text-bg transition-colors ${
                step.status === "active" ? "bg-accent text-bg border-accent" : ""
              }`}
            >
              <div className="font-mono text-[9px] uppercase text-muted">{step.id}</div>
              <div className="font-mono text-[11px] uppercase font-medium">{step.label}</div>
              <div className="font-mono text-[9px] mt-1 opacity-80">{step.detail}</div>
            </a>
            {i < loop.steps.length - 1 && (
              <span className="font-mono text-[14px] text-muted hidden sm:inline" aria-hidden>
                →
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <div className="border border-fg p-3">
          <div className="font-mono text-[10px] uppercase text-muted">Orphans</div>
          <div className="font-display text-t-32 font-extrabold">{loop.stats.orphanCount}</div>
        </div>
        <div className="border border-fg p-3">
          <div className="font-mono text-[10px] uppercase text-muted">Autoresearch kept</div>
          <div className="font-display text-t-32 font-extrabold">{loop.stats.autoresearchKept}</div>
        </div>
        <div className="border border-fg p-3">
          <div className="font-mono text-[10px] uppercase text-muted">Low score (&lt;70%)</div>
          <div className="font-display text-t-32 font-extrabold">{loop.stats.lowScoreCount}</div>
        </div>
      </div>

      {loop.suggestedNext.length > 0 && (
        <div>
          <h3 className="font-mono text-[10px] uppercase text-muted mb-2">Suggested next</h3>
          <ul className="space-y-2">
            {loop.suggestedNext.map((s) => (
              <li key={s.action}>
                <a
                  href={s.href}
                  className="flex justify-between gap-2 border border-fg px-3 py-2 font-mono text-[11px] uppercase hover:bg-fg hover:text-bg"
                >
                  <span>{s.action}</span>
                  <span className="text-muted shrink-0">{s.count}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {loop.stats.hubNodes.length > 0 && (
        <div className="mt-4">
          <h3 className="font-mono text-[10px] uppercase text-muted mb-2">Brain hubs (highest degree)</h3>
          <div className="flex flex-wrap gap-1">
            {loop.stats.hubNodes.map((h) => (
              <a
                key={h.id}
                href={`#?skill=${encodeURIComponent(h.id)}`}
                className="font-mono text-[9px] uppercase px-1.5 py-0.5 border border-fg hover:bg-fg hover:text-bg"
              >
                {h.id} · {h.degree}
              </a>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}